/*
 * bw-review-modal.js
 * Skins Judge.me's "Write a review" modal (injected at runtime) to the Betterway review flow design and turns
 * its negative-resolution help screen into our "Not wiping happy?" screen, where "Get it fixed" opens our own
 * dialog (another component listens for the 'bw:get-it-fixed' event).
 *
 * - Product pages only, loaded with defer. No dependencies.
 * - Idempotent: every change checks first, so the MutationObserver can call apply() as often as it likes.
 * - DOM is built with createElement/textContent only.
 * - Judge.me's own behavior is kept: we never remove its elements or handlers, we only hide some and add ours.
 */
(function () {
  'use strict';

  if (window.BWReviewModal) return;

  var P = 'jdgm-write-review-modal';
  var MODAL = '.' + P;
  var FONT_ID = 'bw-review-modal-font';
  var FONT_URL = 'https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght,SOFT,WONK@0,9..144,700,0..100,0..1;1,9..144,700,0..100,0..1&display=swap';
  var GUARANTEE_URL = '/pages/shipping-and-returns';
  var SVG_NS = 'http://www.w3.org/2000/svg';

  var COPY = {
    getItFixed: 'Get it fixed',
    back: 'Back',
    titleA: 'Not wiping happy?',
    titleB1: "Let's ",
    titleFix: 'fix',
    titleB2: ' that.',
    covered: 'Every order is covered by our',
    guarantee: '100-Day Happy Wiping Guarantee',
    newTab: ' (opens in a new tab)',
    tellUs: "Tell us what went wrong and we'll make it right with a ",
    remedy: 'replacement or your money back',
    thanksTitle: 'Thanks for your review',
    otherProducts: 'Review other products in your order'
  };

  var bypassContact = false;
  var helpShownFor = null;
  var helpShownAt = 0;
  var observed = typeof WeakSet === 'function' ? new WeakSet() : null;

  /* ---------- helpers ---------- */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function track(name, props) {
    try {
      if (typeof window.bwTrack === 'function') window.bwTrack(name, props || {});
    } catch (e) { /* analytics must never break the flow */ }
  }

  function urlParam(name) {
    var m = new RegExp('[?&]' + name + '=([^&#]*)').exec(window.location.href);
    if (!m) return null;
    try { return decodeURIComponent(m[1].replace(/\+/g, ' ')); } catch (e) { return m[1]; }
  }

  function productHandle() {
    var m = /\/products\/([^/?#]+)/.exec(window.location.pathname);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function productTitle(modal) {
    var t = modal && modal.querySelector('.' + P + '__product-title');
    var text = t ? t.textContent.replace(/\s+/g, ' ').trim() : '';
    if (text) return text;
    var og = document.querySelector('meta[property="og:title"]');
    return og ? og.getAttribute('content') : document.title;
  }

  function starsValue(stars) {
    if (!stars) return null;
    var input = stars.querySelector('input');
    var v = input ? parseInt(input.value, 10) : NaN;
    if (v >= 1 && v <= 5) return v;
    var on = stars.querySelectorAll('.jdgm-star.jdgm--on').length;
    return on >= 1 && on <= 5 ? on : null;
  }

  function helpRating(modal) {
    var page = modal && modal.querySelector('.' + P + '__page--negative-resolution');
    var v = starsValue(page && page.querySelector('.' + P + '__stars'));
    if (v) return v;
    var p = parseInt(urlParam('judgeme_rating'), 10);
    return p >= 1 && p <= 5 ? p : null;
  }

  function helpProps(modal) {
    return { rating: helpRating(modal), product: productTitle(modal), productHandle: productHandle() };
  }

  function isOpen(modal) {
    return !!modal && modal.style.display !== 'none' && window.getComputedStyle(modal).display !== 'none';
  }

  function loadFont() {
    if (document.getElementById(FONT_ID) || document.querySelector('link[href*="family=Fraunces"]')) return;
    var link = document.createElement('link');
    link.id = FONT_ID;
    link.rel = 'stylesheet';
    link.href = FONT_URL;
    document.head.appendChild(link);
  }

  function arrowIcon() {
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 16 14');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', 'M15 7H1M7 13L1 7L7 1');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '2');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(path);
    return svg;
  }

  function setText(node, text) {
    if (node && node.textContent.trim() !== text) node.textContent = text;
  }

  /* ---------- help screen (2B) ---------- */

  function buildTitle() {
    var h = el('h2', 'bw-jm-low__title');
    h.appendChild(document.createTextNode(COPY.titleA));
    h.appendChild(document.createElement('br'));
    h.appendChild(document.createTextNode(COPY.titleB1));
    h.appendChild(el('i', '', COPY.titleFix));
    h.appendChild(document.createTextNode(COPY.titleB2));
    return h;
  }

  function buildCopy() {
    var p = el('p', 'bw-jm-low__copy');
    var l1 = el('span', 'bw-jm-low__l1');
    l1.appendChild(document.createTextNode(COPY.covered));
    l1.appendChild(el('br', 'bw-jm-only-m'));
    l1.appendChild(document.createTextNode(' '));
    var a = el('a', '', COPY.guarantee);
    a.href = GUARANTEE_URL;
    a.target = '_blank';
    a.rel = 'noopener';
    a.appendChild(el('span', 'bw-jm-sr-only', COPY.newTab));
    l1.appendChild(a);
    l1.appendChild(document.createTextNode('.'));
    var l2 = el('span', 'bw-jm-low__l2');
    l2.appendChild(document.createTextNode(COPY.tellUs));
    l2.appendChild(el('b', '', COPY.remedy));
    l2.appendChild(document.createTextNode('.'));
    p.appendChild(l1);
    p.appendChild(document.createTextNode(' '));
    p.appendChild(l2);
    return p;
  }

  function enhanceHelpScreen(modal) {
    var page = modal.querySelector('.' + P + '__page--negative-resolution');
    if (!page) return;
    var content = page.querySelector('.' + P + '__negative-resolution-content');
    if (!content) return;

    if (!page.classList.contains('bw-jm-low')) page.classList.add('bw-jm-low');

    // Product name above the stars (Judge.me renders it when it knows the product; fill in if not).
    var header = page.querySelector('.' + P + '__page-header');
    if (header && !header.querySelector('.' + P + '__product-title')) {
      var name = el('p', 'bw-jm-pname', productTitle(null));
      header.insertBefore(name, header.firstChild);
    }

    if (!content.querySelector('.bw-jm-low__title')) {
      var anchor = content.querySelector('.' + P + '__negative-resolution-title') || content.firstChild;
      content.insertBefore(buildTitle(), anchor);
      content.insertBefore(buildCopy(), anchor);
    }

    setText(content.querySelector('.' + P + '__btn--contact-us'), COPY.getItFixed);

    // Judge.me's Back is an <a> without href (not reachable by keyboard): keep it hidden as the action target
    // and give people a real button that triggers it.
    var actions = content.querySelector('.' + P + '__negative-resolution-actions');
    var jmBack = actions && actions.querySelector('.' + P + '__btn--back');
    if (actions && jmBack && !actions.querySelector('.bw-jm-back')) {
      var back = el('button', 'bw-jm-back');
      back.type = 'button';
      back.appendChild(arrowIcon());
      back.appendChild(document.createTextNode(COPY.back));
      back.addEventListener('click', function () {
        track('Review Help Back Clicked', helpProps(modal));
        jmBack.click();
      });
      actions.insertBefore(back, actions.firstChild);
    }
    if (jmBack && jmBack.getAttribute('aria-hidden') !== 'true') jmBack.setAttribute('aria-hidden', 'true');
  }

  // One event per visit to the help screen. A visit ends only when another step becomes current, so Judge.me's
  // fade and re-renders never count twice. Judge.me also rebuilds the whole modal 100 to 250 ms after it first
  // opens (later on slow loads), which gives us a new help page element: when the element we counted was replaced
  // without the visit ending, it is the same visit (bounded to 10 s, so a later reopen still counts).
  function trackHelpShown(modal) {
    var page = modal.querySelector('.' + P + '__page--negative-resolution');
    if (!page) return;
    var current = modal.querySelector('.' + P + '__page.current-page');
    var showing = isOpen(modal) && current === page && page.style.display !== 'none';
    if (showing && helpShownFor !== page) {
      var rebuilt = !!helpShownFor && !helpShownFor.isConnected && Date.now() - helpShownAt < 10000;
      helpShownFor = page;
      helpShownAt = Date.now();
      if (!rebuilt) track('Review Help Shown', helpProps(modal));
    } else if (helpShownFor === page && current && current !== page) {
      helpShownFor = null;
    }
  }

  /* ---------- other steps ---------- */

  function enhanceThankYou(modal) {
    var page = modal.querySelector('.' + P + '__page--thank-you');
    if (!page) return;
    setText(page.querySelector('.' + P + '__form--thank-you > .' + P + '__title:not(.' + P + '__other-products-title)'), COPY.thanksTitle);
    setText(page.querySelector('.' + P + '__other-products-title'), COPY.otherProducts);
  }

  // Judge.me hides its close button after step 1; the design keeps it on every step.
  function keepCloseButton(modal) {
    var btn = modal.querySelector('.' + P + '__close-btn');
    if (!btn) return;
    if (btn.getAttribute('aria-hidden') === 'true') btn.removeAttribute('aria-hidden');
    if (btn.getAttribute('tabindex') === '-1') btn.removeAttribute('tabindex');
  }

  function apply(modal) {
    if (!modal || !modal.isConnected) return;
    keepCloseButton(modal);
    enhanceHelpScreen(modal);
    enhanceThankYou(modal);
    if (isOpen(modal)) loadFont();
    trackHelpShown(modal);
  }

  function watch(modal) {
    if (observed) {
      if (observed.has(modal)) return;
      observed.add(modal);
    } else {
      if (modal.getAttribute('data-bw-watched')) return;
      modal.setAttribute('data-bw-watched', '1');
    }
    apply(modal);
    new MutationObserver(function () { apply(modal); }).observe(modal, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden', 'tabindex']
    });
  }

  function scan() {
    var list = document.querySelectorAll(MODAL);
    for (var i = 0; i < list.length; i++) watch(list[i]);
  }

  /* ---------- Contact us -> Get it fixed ---------- */

  function onClick(e) {
    var target = e.target && e.target.closest ? e.target : null;
    if (!target) return;

    var contact = target.closest('.' + P + '__btn--contact-us');
    if (contact) {
      if (bypassContact) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var modal = contact.closest(MODAL);
      var props = helpProps(modal);
      track('Review Help Get It Fixed Clicked', props);
      var detail = {
        rating: props.rating,
        productHandle: props.productHandle,
        productTitle: props.product,
        productUrl: window.location.pathname,
        source: 'judgeme-help',
        handled: false
      };
      try {
        document.dispatchEvent(new CustomEvent('bw:get-it-fixed', { detail: detail }));
      } catch (err) { /* a broken listener must not leave the button dead */ }
      if (!detail.handled) {
        // Nobody took it: let Judge.me do what it always did.
        bypassContact = true;
        try { contact.click(); } finally { bypassContact = false; }
      }
      return;
    }

    var leave = target.closest('.' + P + '__btn--leave-review');
    if (leave) track('Review Help Leave Review Clicked', helpProps(leave.closest(MODAL)));
  }

  /* ---------- public API ---------- */

  window.BWReviewModal = {
    // Closes Judge.me's modal the way its own close button does. Returns true when a modal was open.
    close: function () {
      var list = document.querySelectorAll(MODAL);
      for (var i = 0; i < list.length; i++) {
        if (!isOpen(list[i])) continue;
        var btn = list[i].querySelector('.' + P + '__close-btn');
        if (btn) { btn.click(); return true; }
      }
      return false;
    }
  };

  /* ---------- start ---------- */

  document.addEventListener('click', onClick, true);

  function start() {
    scan();
    // Judge.me prepends the modal to <body> and rebuilds it after every close.
    new MutationObserver(scan).observe(document.body, { childList: true });
  }

  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start);
})();
